import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { auth, db, storage } from "../../services/firebase"
import {
  ref,
  uploadBytesResumable,
  getDownloadURL
} from "firebase/storage"
import {
  doc,
  setDoc,
  updateDoc,
  getDoc,
  addDoc,
  serverTimestamp,
  collection
} from "firebase/firestore"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { useMultiStepForm } from "../../hooks/useMultiStepForm"
import "./style/VerificationPage.scss"

const docTypes = ["passport", "id", "drivingLicence"] as const
type DocType = typeof docTypes[number]

const VerificationPage: React.FC = () => {
  const navigate = useNavigate()
  const [user, setUser] = useState(auth.currentUser)
  const [selected, setSelected] = useState<DocType>("passport")

  const [files, setFiles] = useState<{ [key: string]: File | null }>({
    passport: null,
    front: null,
    back: null
  })

  const [previews, setPreviews] = useState<{ [key: string]: string }>({})
  const [uploading, setUploading] = useState(false)
  const [status, setStatus] = useState<"notVerified" | "pending" | "approved" | "rejected">("notVerified")

  const [personalInfo, setPersonalInfo] = useState({
    name: "",
    address: "",
    city: "",
    country: "",
    phone: ""
  })

  const [investment, setInvestment] = useState({
    budget: "",
    experience: "",
    sourceOfFunds: ""
  })

  // Fetch existing status
  useEffect(() => {
    if (!user) return
    getDoc(doc(db, "users", user.uid)).then((snap) => {
      if (snap.exists()) {
        const data = snap.data() as any
        setStatus(data.status || "notVerified")
      }
    })
  }, [user])

  // Validation
  const validateStep1 = () => {
    const e: { [key: string]: string } = {}
    if (!personalInfo.name) e.name = "Required"
    if (!personalInfo.address) e.address = "Required"
    if (!personalInfo.city) e.city = "Required"
    if (!personalInfo.country) e.country = "Required"
    if (!personalInfo.phone) e.phone = "Required"
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const validateStep2 = () => {
    const e: { [key: string]: string } = {}
    if (!investment.budget) e.budget = "Required"
    if (!investment.experience) e.experience = "Required"
    if (!investment.sourceOfFunds) e.sourceOfFunds = "Required"
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const isStep3Valid = () =>
    selected === "passport"
      ? !!files.passport
      : !!files.front && !!files.back

  const {
    step,
    errors,
    setErrors,
    handleNext,
    handleBack
  } = useMultiStepForm({
    steps: 3,
    validators: {
      1: validateStep1,
      2: validateStep2
    }
  })

  const handlePersonalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setPersonalInfo((prev) => ({ ...prev, [name]: value }))
  }

  const handleInvestmentChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setInvestment((prev) => ({ ...prev, [name]: value }))
  }

  const handleFileChange = (key: string, file: File | null) => {
    setFiles((prev) => ({ ...prev, [key]: file }))
    if (!file) return
    if (file.type.startsWith("image/")) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreviews((prev) => ({ ...prev, [key]: reader.result as string }))
      }
      reader.readAsDataURL(file)
    } else {
      setPreviews((prev) => ({ ...prev, [key]: "pdf" }))
    }
  }

  const renderPreview = (key: string) => {
    if (!previews[key]) return null
    if (previews[key] === "pdf") return <div className="preview">📄 PDF uploaded</div>
    return <img className="preview" src={previews[key]} alt="preview" />
  }

  const renderInputs = () => {
    const accept = ".png,.jpg,.jpeg,.pdf"
    if (selected === "passport") {
      return (
        <label>
          Upload Passport
          <input
            type="file"
            accept={accept}
            onChange={(e) => handleFileChange("passport", e.target.files?.[0] || null)}
          />
          {renderPreview("passport")}
        </label>
      )
    }

    return (
      <>
        <label>
          Upload Front Side
          <input
            type="file"
            accept={accept}
            onChange={(e) => handleFileChange("front", e.target.files?.[0] || null)}
          />
          {renderPreview("front")}
        </label>
        <label>
          Upload Back Side
          <input
            type="file"
            accept={accept}
            onChange={(e) => handleFileChange("back", e.target.files?.[0] || null)}
          />
          {renderPreview("back")}
        </label>
      </>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user || !isStep3Valid()) {
      toast.error("Please upload all required documents")
      return
    }

    const uid = user.uid
    setUploading(true)

    try {
      // Save verification data to user doc
      await updateDoc(doc(db, "users", uid), {
        status: "pending",
        ...personalInfo,
        ...investment,
        submittedAt: serverTimestamp()
      })

      await addDoc(collection(db, "users", uid, "notifications"), {
        message: "✅ Your verification was submitted. Awaiting admin approval.",
        timestamp: serverTimestamp(),
        seen: "No"
      })

      toast.success("✅ Submitted. Admin will review your info soon.")
      setStatus("pending")
    } catch (err) {
      console.error(err)
      toast.error("Submission failed.")
    } finally {
      setUploading(false)
    }
  }

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="form-step">
            <h3>Step 1: Personal Info</h3>
            {["name", "address", "city", "country", "phone"].map((field) => (
              <label key={field}>
                {field[0].toUpperCase() + field.slice(1)}
                <input
                  name={field}
                  value={(personalInfo as any)[field]}
                  onChange={handlePersonalChange}
                />
                {errors[field] && <div className="error">{errors[field]}</div>}
              </label>
            ))}
          </div>
        )
      case 2:
        return (
          <div className="form-step">
            <h3>Step 2: Investment Profile</h3>
            <label>
              Budget (€)
              <input
                name="budget"
                type="number"
                value={investment.budget}
                onChange={handleInvestmentChange}
              />
              {errors.budget && <div className="error">{errors.budget}</div>}
            </label>
            <label>
              Experience
              <select name="experience" value={investment.experience} onChange={handleInvestmentChange}>
                <option value="">Select…</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
              {errors.experience && <div className="error">{errors.experience}</div>}
            </label>
            <label>
              Source of Funds
              <input
                name="sourceOfFunds"
                value={investment.sourceOfFunds}
                onChange={handleInvestmentChange}
              />
              {errors.sourceOfFunds && <div className="error">{errors.sourceOfFunds}</div>}
            </label>
          </div>
        )
      case 3:
        return (
          <div className="form-step">
            <h3>Step 3: Upload Documents</h3>
            <label>
              Document Type
              <select
                value={selected}
                onChange={(e) => setSelected(e.target.value as DocType)}
              >
                {docTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
            </label>
            {renderInputs()}
          </div>
        )
      default:
        return null
    }
  }

  if (!user) return <p>Please log in to verify your account.</p>

  return (
    <section className="verification-page">
      <ToastContainer position="top-right" autoClose={4000} />

      <h2>Account Verification</h2>

      <div className="step-progress">
        {["Personal Info", "Investment", "Documents"].map((label, i) => {
          const idx = i + 1
          return (
            <div
              key={label}
              className={`step ${step === idx ? "active" : ""} ${step > idx ? "complete" : ""}`}
            >
              <div className="circle">{idx}</div>
              <div className="label">{label}</div>
            </div>
          )
        })}
      </div>

      {(status === "notVerified" || status === "rejected") && (
        <form onSubmit={handleSubmit} className="verification-form">
          {renderStep()}

          <div className="step-navigation">
            {step > 1 && <button type="button" onClick={handleBack}>Back</button>}
            {step < 3 ? (
              <button type="button" onClick={handleNext}>Next</button>
            ) : (
              <button
                type="submit"
                disabled={
                  uploading ||
                  (selected === "passport" && !files.passport) ||
                  (selected !== "passport" && (!files.front || !files.back))
                }
              >
                {uploading ? "Uploading…" : "Submit for Verification"}
              </button>
            )}
          </div>
        </form>
      )}

      {status === "approved" && <p className="approved">✅ Your account is verified!</p>}
      {status === "pending" && <p className="pending"><span>⏳ </span> <br></br>Awaiting admin review.</p>}
    </section>
  )
}

export default VerificationPage
